import {
  Alert,
  AlertTitle,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import { FormikProps } from "formik";
import { useMemo, useRef, useState } from "react";

import { AppAlert } from "@/common/components/Alert/AppAlert";
import AppButton from "@/common/components/Button/AppButton";
import AppIconButton from "@/common/components/Button/AppIconButton";
import DataTabular from "@/common/components/DataTabular/DataTabular";
import {
  DamageDetectionItemValues,
  DamageDetectionValues,
  mapItemToInputDto,
} from "@/common/components/Entity/DamageDetection/DamageDetectionCreateUpdate";
import VehicleDamageLink from "@/common/components/Entity/VehicleDamage/VehicleDamageLink";
import {
  GenericDamageViewerMode,
  VehicleVisualModelGenericDamageEditorRefHandle,
  VisualModelEditorOfGenericDamage,
} from "@/common/components/Entity/VehicleVisualModel/Viewer/v2/GenericDamage/VisualModelEditorOfGenericDamage";
import InlineApiEnumValue from "@/common/components/Enum/InlineApiEnumValue";
import FileListView from "@/common/components/Files/FileListView";
import { FormControlPad } from "@/common/components/Form/FormControlPad";
import AppIcon from "@/common/components/Icons/AppIcon";
import MenuWithTrigger from "@/common/components/Menu/MenuWithTrigger";
import ConfirmationModal from "@/common/components/Modals/ConfirmationModal";
import AppTypography from "@/common/components/Text/AppTypography";
import AppTooltip from "@/common/components/Tooltip/AppTooltip";
import { FileItem } from "@/common/fileItem";
import { ArrayHelper } from "@/common/helpers/array";
import { DamageDetectionFormPermissions } from "@/common/helpers/entity/damageDetection";
import { FormikHelper } from "@/common/helpers/formik";
import { IdHelper } from "@/common/helpers/id";
import { TypeHelper } from "@/common/helpers/type";
import { Mapper } from "@/common/mapping/mapper";
import { EntityInputFormInfo } from "@/common/ts/entity";
import { GenericDamage } from "@/common/ts/visualModel";
import { ValidationHelper } from "@/common/validation";
import { DamageDetectionDto, VehicleArea, VehicleVisualModelDto } from "@/core/api/generated/v1";

/** Entity specific type. */
export interface GenericDamageOfDamageDetectionItem extends GenericDamage<DamageDetectionItemValues> {
  data: DamageDetectionItemValues;
}

function mapItemToGenericDamage(
  source: DamageDetectionItemValues,
  extra: { visualModelId: Nil<string> },
  dest?: Partial<GenericDamageOfDamageDetectionItem>,
): GenericDamageOfDamageDetectionItem {
  return Mapper.visualModel.mapDamageDetectionItemValuesToGenericDamage(
    source,
    extra,
    dest,
  ) as GenericDamageOfDamageDetectionItem;
}

interface Props {
  formInfo: EntityInputFormInfo;
  formPerms: DamageDetectionFormPermissions;
  damageDetection: DamageDetectionDto | null | undefined;
  visualModel: VehicleVisualModelDto | null | undefined;
  formikProps: FormikProps<DamageDetectionValues>;
}

// const logPrefix = "DamageDetectionItemsInput";

export function DamageDetectionItemsInput({
  formInfo,
  formPerms,
  damageDetection,
  visualModel,
  formikProps,
}: Props) {
  const { errors, values, setFieldValue } = formikProps;

  const canViewAndEditDamages =
    values.isAnyDamagesOnVehicle && values.vehicleVisualModelId && visualModel;

  const damageEditorRef =
    useRef<VehicleVisualModelGenericDamageEditorRefHandle<GenericDamageOfDamageDetectionItem>>(
      null,
    );

  const [confirmVehicleHasNoNewDamageOpen, setConfirmVehicleHasNoNewDamageOpen] = useState(false);

  const genericDamages = useMemo<GenericDamageOfDamageDetectionItem[] | null>(
    () =>
      values.items
        ?.map((x) =>
          Mapper.visualModel.mapDamageDetectionItemValuesToGenericDamage(x, {
            visualModelId: visualModel?.id,
          }),
        )
        .filter((x) => x && x.data)
        .map((x) => ({ ...x, data: x.data! })) || null,
    [visualModel, values.items],
  );

  const onItemsChange = (newValue: Nil<DamageDetectionItemValues[]>) => {
    setFieldValue("items", newValue);
  };

  return (
    <Stack>
      {/* Damage editor */}
      <VisualModelEditorOfGenericDamage
        ref={damageEditorRef}
        mode={canViewAndEditDamages ? GenericDamageViewerMode.Edit : GenericDamageViewerMode.View}
        vehicleId={values.vehicleId}
        visualModelId={undefined}
        visualModel={visualModel}
        damages={genericDamages || []}
        errors={
          FormikHelper.isErrorsArray(errors?.items)
            ? errors.items.map((x) => ({
                partTypeId: x?.partTypeId,
                damageTypeId: x?.damageTypeId,
                area: x?.area,
                projection: x?.projection,
                point: x?.point,
                notes: x?.notes,
                attachments: x?.attachments,
              }))
            : []
        }
        isDisplayOld
        isDisplayList={false}
        isViewOldEnabled
        isCreateEnabled={formPerms.canEdit && !values.isAttachmentFilesUploading}
        isUpdateEnabled={formPerms.canEdit && !values.isAttachmentFilesUploading}
        isDeleteEnabled={formPerms.canEdit && !values.isAttachmentFilesUploading}
        isMoveEnabled={formPerms.canEdit && !values.isAttachmentFilesUploading}
        isSplitEnabled={formPerms.canEdit && !values.isAttachmentFilesUploading}
        isMergeEnabled={formPerms.canEdit && !values.isAttachmentFilesUploading}
        oldDamagesDisplay={{
          isStateTogglesEnabled: true,
        }}
        createUpdateProps={{
          isPartTypeDisabled: !formPerms.canEdit,
          isDamageTypeDisabled: !formPerms.canEdit,
          isSubmitDisabled: values.isAttachmentFilesUploading,
        }}
        deleteProps={{
          confirmationModal: {
            bodyAfter: (damage) => (
              <>
                {formInfo.isUpdate && damage.vehicleDamageId && (
                  <Box>
                    {/* <strong>
                      {`NB: This damage won't be automatically deleted from the vehicle history.`} If
                      you wish to do so, you should do it manually on the damage page{" "}
                      <VehicleDamageLink
                        vehicleId={damageItemToDelete.damage.vehicleId}
                        entity={damageItemToDelete.damage}
                        entityId={undefined}
                      />
                      .
                    </strong> */}

                    <Alert severity='warning'>
                      <AlertTitle>Keep in mind:</AlertTitle>

                      <Box component='ol' sx={{ m: 0 }}>
                        <li>
                          The corresponding damage undefined
                          <VehicleDamageLink
                            vehicleId={undefined}
                            entity={undefined}
                            entityId={damage.vehicleDamageId}
                          />{" "}
                          will be deleted from the vehicle history after submitting the form.
                        </li>
                        <li>
                          There might be existing operations already referencing the damage and its
                          deletion may cause data inconsistencies. You may need to revise those
                          operations first.
                        </li>
                      </Box>
                    </Alert>
                  </Box>
                )}
              </>
            ),
          },
        }}
        visualModelViewerProps={{
          slots: {
            topRow: {
              item1: (
                <AppAlert severity='text'>
                  <Box>Click on the vehicle to add the damage.</Box>
                  <Box>Zoom in/out to add damage point precisely.</Box>
                  <Box>Change the vehicle side by clicking preview icon.</Box>
                </AppAlert>
              ),
              item2: (
                <FormControl margin='none'>
                  <FormControlPad padding='normal'>
                    <FormControlLabel
                      sx={{ width: "fit-content" }}
                      label='Vehicle has new damages'
                      control={
                        <Checkbox
                          sx={{ my: -1, p: 1 }}
                          checked={values.isAnyDamagesOnVehicle}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFieldValue("isAnyDamagesOnVehicle", true);
                              setFieldValue(
                                "items",
                                damageDetection?.items?.map((x) => mapItemToInputDto(x)) || [],
                              );
                            } else {
                              setConfirmVehicleHasNoNewDamageOpen(true);
                            }
                          }}
                        />
                      }
                    />
                    {errors?.isAnyDamagesOnVehicle && (
                      <FormHelperText error>{errors?.isAnyDamagesOnVehicle}</FormHelperText>
                    )}
                  </FormControlPad>
                </FormControl>
              ),
            },
            addDamageControls: canViewAndEditDamages && (
              <Stack
                direction={{ xxs: "column", lg: "row" }}
                sx={{ alignItems: { xxs: "flex-start" }, gap: 1 }}
              >
                <AppButton
                  variant='contained'
                  color='primary'
                  size='medium'
                  startIcon={<AppIcon of='add' />}
                  onClick={() => {
                    const newItem: DamageDetectionItemValues = {
                      id: IdHelper.newTempFeId(),
                      area: VehicleArea.Exterior,
                    };
                    const genericDamage =
                      Mapper.visualModel.mapDamageDetectionItemValuesToGenericDamage(newItem, {
                        visualModelId: visualModel?.id,
                      });
                    damageEditorRef.current?.create(genericDamage);
                  }}
                >
                  Add exterior damage
                </AppButton>

                <AppButton
                  variant='contained'
                  color='primary'
                  size='medium'
                  startIcon={<AppIcon of='add' />}
                  onClick={() => {
                    const newItem: DamageDetectionItemValues = {
                      id: IdHelper.newTempFeId(),
                      area: VehicleArea.Interior,
                    };
                    const genericDamage = mapItemToGenericDamage(newItem, {
                      visualModelId: visualModel?.id,
                    });
                    damageEditorRef.current?.create(genericDamage);
                  }}
                >
                  Add interior damage
                </AppButton>
              </Stack>
            ),
          },
        }}
        onCreated={(newValue) => {
          const newItem = Mapper.visualModel.mapGenericDamageToDamageDetectionItemValues(newValue);
          onItemsChange([...(values.items || []), newItem]);
        }}
        onUpdated={(newValue) => {
          const newItem = Mapper.visualModel.mapGenericDamageToDamageDetectionItemValues(
            newValue,
            newValue.data,
          );
          const newItems = values.items?.map((x) => (x.id === newValue.id ? newItem : x));

          onItemsChange(newItems);
        }}
        onDeleted={(newValue) => {
          const newItem = Mapper.visualModel.mapGenericDamageToDamageDetectionItemValues(
            newValue,
            newValue.data,
          );
          const newItems = values.items?.filter((x) => x.id !== newItem.id);
          onItemsChange(newItems);
        }}
        onManyChanged={(params) => {
          let newItems = [...values.items];

          // added
          if (TypeHelper.isNotEmptyArray(params.added)) {
            const itemsAdded = params.added.map((x) =>
              Mapper.visualModel.mapGenericDamageToDamageDetectionItemValues(x),
            );
            newItems = newItems.concat(itemsAdded);
          }

          // updated
          if (TypeHelper.isNotEmptyArray(params.updated)) {
            const itemsUpdated = params.updated.map((x) =>
              Mapper.visualModel.mapGenericDamageToDamageDetectionItemValues(x, x.data),
            );
            newItems = newItems.map((item) => {
              const itemUpdated = itemsUpdated.find((x) => x.id === item.id);
              return itemUpdated ? itemUpdated : item;
            });
          }

          // removed
          if (TypeHelper.isNotEmptyArray(params.removed)) {
            const removedIds = params.removed?.map((x) => x.id);
            newItems = newItems.filter((item) => !removedIds.includes(item.id || ""));
          }

          console.log("DD.onManyChanged", {
            params,
            items: values.items,
            newItems,
          });

          onItemsChange(newItems);
        }}
      />

      {/* Items list */}
      {canViewAndEditDamages && ArrayHelper.isNotEmpty(values.items) && (
        <DataTabular
          sx={{ mt: 2 }}
          columns={[
            {
              field: "partType",
              title: "Part type",
              flex: 1,
              renderCell: (item) => (
                <Stack direction='row' spacing={1} sx={{ alignItems: "center" }}>
                  <Box>{item.partTypeLocal?.name}</Box>

                  <Typography component='div' variant='body2' color='text.secondary'>
                    (<InlineApiEnumValue type='VehicleArea' value={item.area} /> /{" "}
                    {(item.projection && (
                      <InlineApiEnumValue type='VehicleProjection' value={item.projection} />
                    )) ||
                      "-"}
                    )
                  </Typography>
                </Stack>
              ),
            },
            {
              field: "damageType",
              title: "Damage type",
              flex: 1,
              renderCell: (item) => <>{item?.damageTypeLocal?.name}</>,
            },
            {
              field: "damage.id",
              title: "Damage",
              flex: 1,
              renderCell: (item) =>
                item.damage ? (
                  <VehicleDamageLink
                    vehicleId={item.damage?.vehicleId}
                    entity={item.damage}
                    inlineProps={{ withIcon: true }}
                  />
                ) : (
                  "-"
                ),
            },
            {
              field: "notes",
              title: "Notes",
              width: 200,
              renderCell: (item) => (
                <AppTooltip title={item.notes || ""}>
                  <AppTypography ellipsing={{ enabled: true }}>{item.notes}</AppTypography>
                </AppTooltip>
              ),
            },
            {
              field: "attachments",
              title: "Attachments",
              width: 120,
              renderCell: (item) => (
                <>
                  {item.attachments && item.attachments?.length !== 0 && (
                    <FileListView
                      files={FileItem.createManyFrom(item.attachments)}
                      maxVisibleFiles={2}
                      itemWidth={50}
                      itemHeight={30}
                      itemActions={() => ({
                        enabled: true,
                        click: true,
                        download: true,
                      })}
                      fileItemViewProps={{
                        sizeVariant: "compact",
                      }}
                    />
                  )}
                </>
              ),
            },
          ]}
          rows={values.items}
          getRowId={(item) => item.id!}
          borderVariant='bordered'
          rowHeight={65}
          isColumnSelectorEnabled={false}
          isColumnMenuEnabled={false}
          isPaginationEnabled={false}
          renderRowAction={({ item }) => (
            <MenuWithTrigger
              withAuthCloseOnClick
              trigger={
                <AppIconButton>
                  <AppIcon of='moreVert' />
                </AppIconButton>
              }
            >
              {formPerms.canEditItem && (
                <MenuItem
                  onClick={() => {
                    const genericDamage = mapItemToGenericDamage(item, {
                      visualModelId: visualModel?.id,
                    });
                    damageEditorRef.current?.update(genericDamage);
                  }}
                >
                  <ListItemIcon>
                    <AppIcon of='edit' fontSize='small' />
                  </ListItemIcon>
                  <ListItemText>Edit</ListItemText>
                </MenuItem>
              )}

              {formPerms.canDeleteItem && (
                <MenuItem
                  onClick={() => {
                    const genericDamage = mapItemToGenericDamage(item, {
                      visualModelId: visualModel?.id,
                    });
                    damageEditorRef.current?.delete(genericDamage);
                  }}
                >
                  <ListItemIcon>
                    <AppIcon of='delete' fontSize='small' />
                  </ListItemIcon>
                  <ListItemText>Delete</ListItemText>
                </MenuItem>
              )}
            </MenuWithTrigger>
          )}
        />
      )}

      {canViewAndEditDamages && errors?.items && (
        <FormControl margin='dense' fullWidth error>
          <FormHelperText error>Some items have errors. Please review them.</FormHelperText>

          <FormHelperText error>
            {ValidationHelper.getFormikErrorsAsString(errors.items, {
              isIncludeNested: false,
            })}
          </FormHelperText>
        </FormControl>
      )}

      {/* Confirm no damages modal. */}
      <ConfirmationModal
        title='Vehicle has no new damages?'
        body={"Please confirm that you haven't detected any new damages during the inspection."}
        open={confirmVehicleHasNoNewDamageOpen}
        onClose={() => setConfirmVehicleHasNoNewDamageOpen(false)}
        onCancel={() => setConfirmVehicleHasNoNewDamageOpen(false)}
        onConfirm={() => {
          setFieldValue("isAnyDamagesOnVehicle", false);
          setFieldValue("items", []);
          setConfirmVehicleHasNoNewDamageOpen(false);
        }}
      />
    </Stack>
  );
}
